import {
  ErrorModal,
  MesfModal,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Autocomplete, Button, Grid, TextField } from "@mui/material";
import { get, isNil } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../../controls/buttons/FooterModalButtons";
import { MaterialAutocomplete } from "../../../../../../shared/autocompletes/MaterialAutocomplete";
import { DateTimePickerDE } from "../../../../../../shared/data-entries/common/DateTimePickerDE";
import { modalStyles } from "@/shared/styles/modalStyles";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useEAFDataEntryActions } from "../../../hooks/useEAFDataEntryActions";
import { Addition } from "../../../models/eaf.models";
import { validateTimestamp } from "@/utils/validations";

type Props = {
  showModal: "NEW" | "EDIT" | "";
  onHide: () => void;
  addition: Addition | undefined;
};

export const AdditionModal = ({ showModal, onHide, addition }: Props) => {
  const { state } = useUTLSettingsContext();
  const maxAdditionWeight = parseInt(
    get(state.settings, "EAF_DE_ADDITIONS_MAX_WEIGHT", 5000),
  );

  const { saveAddition } = useEAFDataEntryActions();
  const {
    state: { deProcess },
  } = useDataEntryContext();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Addition>({});

  const onSubmit: SubmitHandler<Addition> = async (data: Addition) => {
    if (data.Weight !== null && data.Weight > maxAdditionWeight) {
      setPendingData(data);
      setShowWarningModal(true);
      return;
    }
    setIsSubmitLoading(true);
    await saveAddition(data);
    setIsSubmitLoading(false);
    onHide();
  };

  useEffect(() => {
    if (showModal !== "") {
      if (showModal === "NEW") {
        reset({ Timestamp: deProcess?.process.Start });
      } else if (addition) {
        const formattedWeight = !isNil(addition?.Weight)
          ? parseFloat(addition?.Weight.toFixed(1))
          : null;
        reset({ ...addition, Weight: formattedWeight });
      }
    }
  }, [showModal, deProcess?.process.Start, addition, reset]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            title={`${showModal} MANUAL ADDITION`}
            open={showModal !== ""}
            handleClose={() => onHide()}
            id="addition-modal"
            maxWidth="sm"
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <MesfModal.Content dividers sx={modalStyles.modalContent}>
                <Grid container spacing={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="Timestamp"
                        control={control}
                        rules={{
                          required: "Timestamp is required",
                          validate: (value) =>
                            validateTimestamp(value, deProcess?.process.Start),
                        }}
                        render={({ field }) => (
                          <DateTimePickerDE
                            {...field}
                            format={"HH:mm"}
                            disableOpenPicker
                            label="Time"
                            fieldError={errors.Timestamp}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="MaterialId"
                        control={control}
                        rules={{ required: "Material is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <MaterialAutocomplete
                            label="Material"
                            value={field.value}
                            onChange={field.onChange}
                            fieldError={error}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="Weight"
                        control={control}
                        rules={{ required: "Weight is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            type="number"
                            variant="outlined"
                            fullWidth
                            label="Weight [lbs]"
                            error={!!error}
                            helperText={error?.message}
                            inputProps={{
                              step: 0.1,
                              min: 0.0,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="Source"
                        control={control}
                        rules={{ required: "Source is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <Autocomplete
                            id="source-selector"
                            options={["BIN", "PAN"]}
                            value={field.value || null}
                            onChange={(event, newValue) => {
                              field.onChange(newValue);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Source"
                                variant="outlined"
                                fullWidth
                                error={!!error}
                                helperText={error?.message}
                                slotProps={{
                                  inputLabel: {
                                    sx: {
                                      fontSize: "clamp(.7rem,.8rem,1rem)",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </MesfModal.Content>
              <MesfModal.Actions sx={modalStyles.modalActions}>
                <FooterModalButtons
                  isSubmitLoading={isSubmitLoading}
                  onHide={onHide}
                />
              </MesfModal.Actions>
            </form>
          </MesfModal>
        </Grid>
      </Grid>
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error Creating Manual Probe"
      />
      <MesfModal
        open={showWarningModal}
        handleClose={() => setShowWarningModal(false)}
        title="Warning"
        id="warningModal"
      >
        <MesfModal.Content>
          <p>
            Weight value is above ({maxAdditionWeight}). Do you want to proceed?
          </p>
        </MesfModal.Content>
        <MesfModal.Actions>
          <Button
            fullWidth
            variant="contained"
            color="inherit"
            onClick={() => setShowWarningModal(false)}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={async () => {
              setShowWarningModal(false);
              if (pendingData) {
                setIsSubmitLoading(true);
                await saveAddition(pendingData);
                setIsSubmitLoading(false);
                onHide();
                setPendingData(null);
              }
            }}
          >
            Proceed
          </Button>
        </MesfModal.Actions>
      </MesfModal>
    </>
  );
};
